import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { ItermediateAccount, initialUserAccount, AccountForChange } from '@/interfaces/account';
import userServices from '@/api/userServices';

interface AccountsState {
  currentAccount: ItermediateAccount;
  value: ItermediateAccount[];
  visibleAccounts: ItermediateAccount[];
  loading: boolean;
  error: string;
}

const initialState: AccountsState = {
  currentAccount: initialUserAccount,
  value: [],
  visibleAccounts: [],
  loading: false,
  error: '',
};

export const fetchAccounts = createAsyncThunk('accounts/fetch', async (_, thunkAPI) => {
  try {
    const result = await userServices.accounts();
    return result;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const updateAccount = createAsyncThunk('account/update', async ({ id, data }: { id: string; data: any }, thunkAPI) => {
  try {
    const result = await userServices.updateAccountName(id, data);
    return result;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const updateDisplaying = createAsyncThunk('account/updateDisplaying', async (data: any, thunkAPI) => {
  try {
    const result = await userServices.updateAccountDisplaying(data);
    return result;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const accountsSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    refresh: () => initialState,

    setCurrentAccount: (state, action: PayloadAction<ItermediateAccount>) => {
      state.currentAccount = action.payload;
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchAccounts.pending, state => {
      state.loading = true;
    });

    builder.addCase(fetchAccounts.fulfilled, (state, action) => {
      const {
        payload: { userAccounts },
      } = action;
      state.value = userAccounts;
      state.visibleAccounts = userAccounts.filter((acc: ItermediateAccount) => acc.displayed);
      if (!state.currentAccount.accountId) {
        const selectedAccount = state.visibleAccounts.find(acc => acc.accountNumber === localStorage.getItem('accountNumber'));
        state.currentAccount = selectedAccount || state.visibleAccounts[0];
      } else {
        state.currentAccount = userAccounts.find((acc: ItermediateAccount) => acc.accountId === state.currentAccount.accountId);
      }
      state.loading = false;
    });

    builder.addCase(fetchAccounts.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });

    builder.addCase(updateAccount.pending, state => {
      state.loading = true;
    });

    builder.addCase(updateAccount.fulfilled, (state, action) => {
      const { payload } = action;
      state.value = state.value.map(el => (el.accountId === payload.accountId ? { ...el, ...payload } : el));
      state.visibleAccounts = state.visibleAccounts.map(el => (el.accountId === payload.accountId ? { ...el, ...payload } : el));

      if (state.currentAccount.accountId === payload.accountId) {
        state.currentAccount.name = payload.name;
      }
      state.loading = false;
    });

    builder.addCase(updateAccount.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });

    builder.addCase(updateDisplaying.pending, state => {
      state.loading = true;
    });

    builder.addCase(updateDisplaying.fulfilled, (state, action) => {
      const accountsForUpdate = action.meta.arg.userAccounts;

      state.value = state.value.map(acc => {
        const accForUpdate = accountsForUpdate.find((el: AccountForChange) => el.id === acc.accountId);
        return accForUpdate ? { ...acc, displayed: !acc.displayed } : acc;
      });
      state.visibleAccounts = state.value.filter(acc => acc.displayed);
      accountsForUpdate.forEach((el: AccountForChange) => {
        if (el.id === state.currentAccount.accountId) {
          state.currentAccount = state.visibleAccounts[0];
        }
      });
      state.loading = false;
    });

    builder.addCase(updateDisplaying.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });
  },
});

export const { refresh, setCurrentAccount } = accountsSlice.actions;

export const selectAccounts = (state: RootState) => state.accounts;

export default accountsSlice.reducer;
